import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { NavbarLogo } from 'assets';

import { ReactComponent as ArrowLeftIcon } from 'assets/images/svg/arrow-left.svg';
import { ReactComponent as ProfileIcon } from 'assets/images/svg/user-profile-icon.svg';
import { ReactComponent as HelpIcon } from 'assets/images/svg/help.svg';
import { ReactComponent as HelpLightIcon } from 'assets/images/svg/help-light.svg';

import container from 'containers/HeaderMenuContainer';
import NavItemsDesktop from 'components/layout/navbar/NavItemsDesktop';
import NavItemsMobile from 'components/layout/navbar/NavItemsMobile';

import {
  ACCOUNT_SUMMARY,
  HOME,
  MY_PROFILE,
  SIGN_IN,
  SUPPORT,
} from 'utils/routes';
import { useScreen } from 'hooks/useDimensions';
import siteStyle, { M } from 'utils/siteStyle';
import { features } from 'utils/features';

const Navbar = ({
  isLoggedIn,
  avatarUrl,
  hardResetFilters,
  goBack,
  goTo,
  wrapperStyles,
  backTo,
  backToMobile,
  navbarLargeClassname,
  customNavEl,
}) => {
  const { isMobileScreen, isDesktopScreen } = useScreen();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navMenu = useRef(null);

  const backToUrl = useMemo(
    () => (isMobileScreen ? (backToMobile || backTo) : backTo),
    [isMobileScreen, backTo, backToMobile],
  );

  const onToggleNavMenu = () => {
    setIsMenuOpen((prev) => !prev);
    document.body.classList.toggle('overflow-hidden');
    navMenu.current.scrollTo(0, 0);
  };

  useEffect(() => {
    if (isDesktopScreen && isMenuOpen) {
      onToggleNavMenu();
    }
  }, [isDesktopScreen, isMenuOpen]);

  const handleProfileClick = (myProfilePath) => {
    goTo(myProfilePath);
  };

  const renderNavbarLogo = !isMenuOpen && !backToUrl && (
    <a href={HOME} aria-label="home" onClick={hardResetFilters}>
      <NavbarLogo />
    </a>
  );

  const renderBurgerButton = (
    <div className="p-sm md:pl-0">
      <button
        type="button"
        aria-label="burger-menu"
        className="w-5 h-5 flex flex-col justify-between focus:outline-none cursor-pointer"
        onClick={onToggleNavMenu}
      >
        <div className={classNames('w-5 h-0.5 transition-transform duration-500  rounded-1 my-0', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), { '-rotate-45 translate-x-[-5px] translate-y-[8.8px]': isMenuOpen })} />
        <div className={classNames('w-5 h-0.5 transition-opacity duration-500 rounded-1 my-0', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), { 'opacity-0': isMenuOpen })} />
        <div className={classNames('w-5 h-0.5 transition-transform duration-500 rounded-1 my-0', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), { 'rotate-45 translate-x-[-5px] translate-y-[-8.8px]': isMenuOpen })} />
      </button>
    </div>
  );

  const renderBackButton = backToUrl && (
    <div className="flex items-center pl-sm gap-5">
      <div className="w-px h-8 bg-dark" />
      <button type="button" className="focus:outline-none" onClick={() => goBack(backToUrl || null)}>
        <ArrowLeftIcon className={siteStyle({
          common: 'h-4 fill-current',
          gb: 'text-white',
          rg: 'text-white',
          default: 'text-black',
        })}
        />
      </button>
    </div>
  );

  const rendeUserProfile = !isMenuOpen && !backToUrl && (
    <button
      className="focus:outline-none"
      onClick={() => handleProfileClick(isLoggedIn ? ACCOUNT_SUMMARY : SIGN_IN)}
    >
      {!isLoggedIn && avatarUrl
        ? <img src={avatarUrl} alt="profile avatar" className="w-10 h-10 object-cover object-center rounded-full" />
        : (
          <div className="w-10 h-10 flex justify-center items-center bg-white border border-light-grey-four rounded-full">
            <ProfileIcon className="w-5 h-5 fill-current text-dark" />
          </div>
        )}
    </button>
  );

  const renderNavMenuDrawer = (
    <div
      ref={navMenu}
      style={{ zIndex: 9999 }}
      className={classNames('fixed w-full inset-0 top-14 duration-500 bg-white transition-max-height overflow-y-auto md:top-18', {
        'max-h-screen ease-out': isMenuOpen,
        'max-h-0': !isMenuOpen,
      })}
    >
      <NavItemsMobile toggleMenu={onToggleNavMenu} />
    </div>
  );

  const renderProfileOptions = !isMenuOpen && !backToUrl && (
    <div className="flex items-center gap-4">
      <button id="navbar-desktop-help" onClick={() => goTo(SUPPORT)} className="flex items-center gap-1 focus:outline-none">
        {M.GB ? <HelpLightIcon className="w-5 h-5" /> : <HelpIcon className="w-5 h-5" />}
        <p
          className={
            siteStyle({
              common: 'text-4 leading-5 -tracking-0.2 font-primary',
              gb: 'text-white',
              rg: 'text-white',
              default: 'text-dark',
            })
          }
        >
          Help
        </p>
      </button>
      <div className="w-px h-7 bg-light-grey-four" />
      <button
        id="navbar-desktop-sign-in-or-profile"
        onClick={() => handleProfileClick(isLoggedIn ? MY_PROFILE : SIGN_IN)}
        className="flex items-center h-10 gap-1 py-sm px-2 bg-white border border-light-grey-four rounded-2 focus:outline-none"
      >
        {isLoggedIn && avatarUrl
          ? <img src={avatarUrl} alt="profile avatar" className="w-5 h-5 object-cover object-center rounded-full" />
          : <ProfileIcon className="w-5 h-5 fill-current text-dark" />}
        <p className="text-4 leading-5 -tracking-0.2 text-dark font-primary-two">
          {isLoggedIn ? 'Account' : 'Sign in'}
        </p>
      </button>
    </div>
  );

  const renderNavbarSmall = (
    <div className="w-full h-full flex justify-between items-center lg:hidden">
      <div className={classNames('flex items-center', { 'gap-1.5 md:gap-4': !backToUrl })}>
        {renderBurgerButton}
        {renderBackButton}
        {renderNavbarLogo}
      </div>
      {isMobileScreen ? rendeUserProfile : renderProfileOptions}
      {renderNavMenuDrawer}
    </div>
  );

  const renderNavbarLarge = (
    <div className={navbarLargeClassname}>
      <div className="flex items-center justify-between">
        {renderNavbarLogo}
        {renderProfileOptions}
      </div>
      {features.hasNavMenu && <NavItemsDesktop wrapperClassname="pt-6" />}
    </div>
  );

  const renderCustomNavbar = (
    <div className="w-full h-full flex justify-between items-center lg:hidden">
      <div className="flex items-center gap-1">
        {customNavEl?.startEl || renderBurgerButton}
        {customNavEl?.backToEl && !isMenuOpen && <div className="w-px h-5 bg-light-grey-four" />}
        {customNavEl?.backToEl && !isMenuOpen && customNavEl?.backToEl}
      </div>
      {!isMenuOpen && customNavEl?.centerEl}
      {(!isMenuOpen && customNavEl?.endEl) || <div className="w-9 h-9" />}
      {renderNavMenuDrawer}
    </div>
  );

  return (
    <section
      id="main-top-navbar"
      className={classNames('z-100 w-full flex justify-center md:h-18 md:px-10 lg:h-auto lg:px-14 lg:py-4 lg:z-40', wrapperStyles, siteStyle({ gb: 'bg-secondary', rg: 'bg-primary !border-transparent', default: 'bg-white' }), {
        'border-b border-light-grey-four': !isMenuOpen,
      })}
    >
      {customNavEl && isMobileScreen ? renderCustomNavbar : renderNavbarSmall}
      {renderNavbarLarge}
    </section>
  );
};

Navbar.defaultProps = {
  isLoggedIn: false,
  avatarUrl: '',
  backTo: null,
  backToMobile: null,
  navbarLargeClassname: 'hidden w-full max-w-300 lg:block',
  wrapperStyles: 'h-14 px-4 py-2 relative',
  customNavEl: null,
};

Navbar.propTypes = {
  navbarLargeClassname: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  avatarUrl: PropTypes.string,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }).isRequired,
  goTo: PropTypes.func.isRequired,
  hardResetFilters: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  backTo: PropTypes.string,
  backToMobile: PropTypes.string,
  wrapperStyles: PropTypes.string,
  customNavEl: PropTypes.shape({
    startEl: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
    backToEl: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
    centerEl: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
    endEl: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  }),
};

export default container(Navbar);
